import type {
  ServiceRequestTransferOptionsReadModel,
  ServiceRoutingRole,
} from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { ArrowRightLeft, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getServiceRequestTransferOptions, type OperatorIdentity } from "./api";
import type {
  ServiceRequestTransferFrozenCommand,
  ServiceRequestTransferRecovery,
  ServiceRequestTransferRecoveryView,
} from "./serviceRequestTransferRecovery";

const roleFallbackLabels: Record<ServiceRoutingRole, string> = {
  MANAGER: "店长",
  CASHIER: "收银员",
  WAITER: "服务员",
};

function transferOptionsQueryKey(operator: OperatorIdentity, eventId: string): readonly unknown[] {
  return [
    "service-request-transfer-options",
    operator.tenantId,
    operator.storeId,
    operator.id,
    eventId,
  ] as const;
}

function hasChineseReason(value: string): boolean {
  const normalized = value.trim();
  return (
    [...normalized].length >= 2 &&
    [...normalized].length <= 300 &&
    /[\u3400-\u9fff]/u.test(normalized)
  );
}

function firstSelectableRole(
  options: ServiceRequestTransferOptionsReadModel | undefined,
): ServiceRoutingRole | "" {
  return options?.roles.find((role) => role.canSelect)?.role ?? "";
}

export function ServiceRequestTransferComposer({
  operator,
  eventId,
  recoveryView,
  pending,
  blocked,
  errorMessage,
  successMessage,
  onSubmit,
  onRetry,
}: {
  operator: OperatorIdentity;
  eventId: string;
  recoveryView: ServiceRequestTransferRecoveryView;
  pending: boolean;
  blocked: boolean;
  errorMessage: string | null;
  successMessage: string | null;
  onSubmit: (command: ServiceRequestTransferFrozenCommand) => void;
  onRetry: (recovery: ServiceRequestTransferRecovery) => void;
}): React.JSX.Element {
  const ownedRecovery =
    recoveryView.status === "OWNED" && recoveryView.recovery.command.eventId === eventId
      ? recoveryView.recovery
      : null;
  const [open, setOpen] = useState(ownedRecovery !== null);
  const options = useQuery({
    queryKey: transferOptionsQueryKey(operator, eventId),
    queryFn: () => getServiceRequestTransferOptions(eventId),
    enabled: open && ownedRecovery === null,
    staleTime: 0,
  });
  const [targetRole, setTargetRole] = useState<ServiceRoutingRole | "">("");
  const [targetCandidateId, setTargetCandidateId] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (ownedRecovery) setOpen(true);
  }, [ownedRecovery]);
  useEffect(() => {
    if (!options.data || ownedRecovery) return;
    setTargetRole((current) =>
      current && options.data.roles.some((role) => role.role === current && role.canSelect)
        ? current
        : firstSelectableRole(options.data),
    );
    setTargetCandidateId("");
  }, [options.data, ownedRecovery]);
  const roleOption = options.data?.roles.find((role) => role.role === targetRole) ?? null;
  const candidates = useMemo(
    () => options.data?.candidates.filter((candidate) => candidate.role === targetRole) ?? [],
    [options.data, targetRole],
  );
  const targetCandidate =
    candidates.find((candidate) => candidate.candidateId === targetCandidateId) ?? null;
  const normalizedReason = reason.trim();
  const canSubmit = Boolean(
    options.data &&
      !options.isError &&
      options.data.canTransfer &&
      roleOption?.canSelect &&
      (targetCandidateId === "" || targetCandidate?.canSelect) &&
      hasChineseReason(reason) &&
      !pending &&
      !blocked &&
      recoveryView.status === "NONE",
  );

  if (!open && recoveryView.status === "NONE") {
    return (
      <button
        className="button secondary compact"
        type="button"
        disabled={blocked}
        onClick={() => setOpen(true)}
      >
        <ArrowRightLeft size={16} aria-hidden="true" /> 安全转交
      </button>
    );
  }

  return (
    <section className="service-request-transfer-panel" aria-label="安全转交现场待办">
      <div className="section-heading">
        <div>
          <strong>安全转交</strong>
          <p>选择接手岗位或具体人员，并说明原因。转交只改变本项负责人，不会完成待办。</p>
        </div>
        {!ownedRecovery ? (
          <button
            className="button ghost compact"
            type="button"
            disabled={pending}
            onClick={() => setOpen(false)}
          >
            取消
          </button>
        ) : null}
      </div>
      {recoveryView.status === "OTHER_OPERATOR" ? (
        <div className="error-band" role="alert">
          另一个账号有尚未确认的转交操作。请切回原账号处理，本账号不能查看目标人员和原因。
        </div>
      ) : recoveryView.status === "RECOVERY_STORAGE_UNAVAILABLE" ? (
        <div className="error-band" role="alert">
          无法安全保存转交进度，已暂停操作。请关闭当前标签页后重新打开。
        </div>
      ) : ownedRecovery ? (
        <div className="service-duty-recovery" role="alert">
          <strong>上次转交结果尚未确认</strong>
          <p>
            目标：{ownedRecovery.command.targetDisplayName}；原因：
            {ownedRecovery.command.reason}
          </p>
          <p>只能使用原岗位、原人员、原原因和原请求重试，不能重新选择后重复提交。</p>
          {errorMessage ? <p>{errorMessage}</p> : null}
          <button
            className="button primary compact"
            type="button"
            disabled={pending}
            onClick={() => onRetry(ownedRecovery)}
          >
            {pending ? "正在核对原请求" : "使用原请求安全重试"}
          </button>
        </div>
      ) : options.isLoading && !options.data ? (
        <div className="empty-state">正在读取可接手岗位和人员</div>
      ) : !options.data || options.isError ? (
        <div className="error-band" role="alert">
          <span>可接手人员暂时无法安全确认，已停止转交。</span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => options.refetch()}
          >
            <RefreshCw size={15} aria-hidden="true" /> 重新读取
          </button>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit || !roleOption) return;
            onSubmit({
              eventId,
              expectedWorkItemVersion: options.data.workItemVersion,
              targetRole: roleOption.role,
              targetCandidateId: targetCandidate?.candidateId ?? null,
              targetDisplayName:
                targetCandidate?.displayName ?? `${roleOption.label}岗位下一位可用人员`,
              reason: normalizedReason,
              idempotencyKey: crypto.randomUUID(),
            });
          }}
        >
          {!options.data.canTransfer ? (
            <div className="error-band">{options.data.transferUnavailableReason}</div>
          ) : null}
          <label>
            接手岗位
            <select
              value={targetRole}
              disabled={!options.data.canTransfer || pending || blocked}
              onChange={(event) => {
                setTargetRole(event.target.value as ServiceRoutingRole);
                setTargetCandidateId("");
              }}
            >
              <option value="">请选择岗位</option>
              {options.data.roles.map((role) => (
                <option key={role.role} value={role.role} disabled={!role.canSelect}>
                  {role.label}（可接手 {role.availableCandidateCount} 人）
                  {!role.canSelect ? `：${role.unavailableReason}` : ""}
                </option>
              ))}
            </select>
            <small className="form-hint">
              {roleOption?.unavailableReason ??
                "选择岗位后，可以交给岗位统一安排，也可以指定一位可用人员。"}
            </small>
          </label>
          <label>
            接手人员
            <select
              value={targetCandidateId}
              disabled={!roleOption?.canSelect || pending || blocked}
              onChange={(event) => setTargetCandidateId(event.target.value)}
            >
              <option value="">由该岗位安排下一位可用人员</option>
              {candidates.map((candidate) => (
                <option
                  key={candidate.candidateId}
                  value={candidate.candidateId}
                  disabled={!candidate.canSelect}
                >
                  {candidate.displayName}
                  {!candidate.canSelect ? `：${candidate.unavailableReason}` : ""}
                </option>
              ))}
            </select>
            <small className="form-hint">不可用人员会保留说明，但不能被选中。</small>
          </label>
          <label>
            转交原因
            <textarea
              value={reason}
              disabled={pending || blocked}
              maxLength={300}
              placeholder="例如：我要下班，请李服务员继续处理"
              onChange={(event) => setReason(event.target.value)}
            />
            <small className="form-hint">必须填写 2–300 字中文原因，接手人可以看到。</small>
          </label>
          {errorMessage ? <div className="error-band">{errorMessage}</div> : null}
          {successMessage ? <output className="success-band">{successMessage}</output> : null}
          <button className="button primary" type="submit" disabled={!canSubmit}>
            <ArrowRightLeft size={16} aria-hidden="true" />
            {pending ? "正在转交" : "确认转交，不完成待办"}
          </button>
        </form>
      )}
    </section>
  );
}

export { hasChineseReason as serviceRequestTransferReasonIsValid };
